import React, { useState } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { Compass, Trophy, Users, ArrowLeft, ArrowRight, CheckCircle2, Shield, Flame, ExternalLink, Sparkles, MapPin, Clock, Check } from 'lucide-react';
import Footer from '@/components/Footer';
import SEOHead from '@/components/SEOHead';

export default function GuidePage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const initialMode = searchParams.get('mode') === 'join' ? 'join' : 'host';
  const [mode, setMode] = useState<'host' | 'join'>(initialMode);
  const [step, setStep] = useState<number>(1);

  // Interactive mock states
  const [mockSport, setMockSport] = useState<string>('cricket');
  const [mockSkill, setMockSkill] = useState<string>('casual');
  const [mockDay, setMockDay] = useState<string>('today');
  const [mockWaiverChecked, setMockWaiverChecked] = useState<boolean>(true);
  const [mockFilter, setMockFilter] = useState<'all' | 'need1'>('need1');

  const totalSteps = 4;

  return (
    <div style={{ minHeight: '100vh', background: '#fbfbfd', color: '#0a0a0b', display: 'flex', flexDirection: 'column' }}>
      <SEOHead
        title="Player Guide & How to Play"
        description="Learn how to find neighborhood sports matches, claim player slots, host games, and play fair on KhelDrop."
        canonical="/guide"
        keywords="kheldrop guide, how to play, sports matchmaking rules, neighborhood arcade guide"
      />
      {/* Top Navbar */}
      <header
        style={{
          borderBottom: '1px solid #e8e8ed',
          background: '#ffffff',
          position: 'sticky',
          top: 0,
          zIndex: 100,
          padding: '14px 24px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
          <button
            onClick={() => navigate('/')}
            style={{
              background: '#f4f4f6',
              border: '1px solid #e8e8ed',
              borderRadius: '10px',
              padding: '6px 12px',
              fontSize: '13px',
              fontWeight: 700,
              color: '#0a0a0b',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
            }}
          >
            <ArrowLeft size={15} /> Back to Games
          </button>
          <div style={{ height: '20px', width: '1px', background: '#e8e8ed' }} />
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <span style={{ fontSize: '18px', fontWeight: 900, letterSpacing: '-0.03em' }}>KhelDrop</span>
            <span style={{ fontSize: '11px', fontWeight: 800, background: '#7c3aed', color: '#ffffff', padding: '2px 8px', borderRadius: '6px' }}>
              OFFICIAL PLAYBOOK
            </span>
          </div>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <button
            onClick={() => {
              if (typeof window !== 'undefined') {
                window.dispatchEvent(new CustomEvent('kheldrop_start_tour'));
              }
              navigate('/');
            }}
            style={{
              background: 'rgba(124, 58, 237, 0.08)',
              border: '1px solid rgba(124, 58, 237, 0.25)',
              color: '#7c3aed',
              borderRadius: '10px',
              padding: '8px 14px',
              fontWeight: 800,
              fontSize: '13px',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
            }}
          >
            <Compass size={14} /> Interactive Tour
          </button>

          <button
            onClick={() => navigate('/')}
            style={{
              background: '#0a0a0b',
              color: '#ffffff',
              border: 'none',
              borderRadius: '10px',
              padding: '8px 16px',
              fontWeight: 800,
              fontSize: '13px',
              cursor: 'pointer',
            }}
          >
            Open App
          </button>
        </div>
      </header>

      {/* Hero Headline */}
      <div style={{ maxWidth: '860px', margin: '40px auto 20px', padding: '0 20px', textAlign: 'center' }}>
        <div style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', background: 'rgba(124, 58, 237, 0.08)', color: '#7c3aed', padding: '4px 12px', borderRadius: '999px', fontSize: '12px', fontWeight: 800, marginBottom: '14px' }}>
          <Compass size={14} /> HOW KHELDROP WORKS
        </div>
        <h1 style={{ fontSize: '36px', fontWeight: 900, letterSpacing: '-0.03em', margin: '0 0 12px', color: '#111827' }}>
          Your Guide to Hosting & Joining Matches
        </h1>
        <p style={{ fontSize: '16px', color: '#6b7280', margin: 0, lineHeight: 1.6 }}>
          Learn how to organize recreational sports events in your neighborhood, or join an active game needing one more player in under 60 seconds.
        </p>

        {/* Toggle Mode */}
        <div
          style={{
            display: 'inline-flex',
            background: '#e5e7eb',
            padding: '4px',
            borderRadius: '16px',
            margin: '28px auto 0',
            gap: '6px',
          }}
        >
          <button
            onClick={() => { setMode('host'); setStep(1); }}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              padding: '10px 22px',
              borderRadius: '12px',
              border: 'none',
              background: mode === 'host' ? '#ffffff' : 'transparent',
              color: mode === 'host' ? '#111827' : '#6b7280',
              fontWeight: 800,
              fontSize: '14px',
              cursor: 'pointer',
              boxShadow: mode === 'host' ? '0 2px 8px rgba(0,0,0,0.06)' : 'none',
              transition: 'all 0.15s ease',
            }}
          >
            <Trophy size={16} color={mode === 'host' ? '#7c3aed' : '#6b7280'} />
            Host an Event (4 Steps)
          </button>

          <button
            onClick={() => { setMode('join'); setStep(1); }}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              padding: '10px 22px',
              borderRadius: '12px',
              border: 'none',
              background: mode === 'join' ? '#ffffff' : 'transparent',
              color: mode === 'join' ? '#111827' : '#6b7280',
              fontWeight: 800,
              fontSize: '14px',
              cursor: 'pointer',
              boxShadow: mode === 'join' ? '0 2px 8px rgba(0,0,0,0.06)' : 'none',
              transition: 'all 0.15s ease',
            }}
          >
            <Users size={16} color={mode === 'join' ? '#10b981' : '#6b7280'} />
            Join a Match (4 Steps)
          </button>
        </div>
      </div>

      {/* Main Interactive Guide Container */}
      <main style={{ maxWidth: '860px', width: '100%', margin: '0 auto 60px', padding: '0 20px', flex: 1 }}>
        <div
          style={{
            background: '#ffffff',
            borderRadius: '24px',
            border: '1px solid #e5e7eb',
            boxShadow: '0 10px 30px rgba(0,0,0,0.04)',
            overflow: 'hidden',
          }}
        >
          {/* Step Pill Header */}
          <div style={{ padding: '20px 24px', borderBottom: '1px solid #f3f4f6', background: '#fafafa' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px', overflowX: 'auto' }}>
              {[1, 2, 3, 4].map((s) => {
                const label = mode === 'host'
                  ? ['Sport & Skill', 'Venue & Time', 'Slots & Rules', 'Publish Lobby'][s - 1]
                  : ['Radar & Filters', 'Match Intel', 'Safety Waiver', 'Lock & Play'][s - 1];
                const active = step === s;
                const done = step > s;
                return (
                  <button
                    key={s}
                    onClick={() => setStep(s)}
                    style={{
                      flex: 1,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '8px',
                      padding: '10px 12px',
                      borderRadius: '12px',
                      border: active ? '2px solid #111827' : '1px solid #e5e7eb',
                      background: active ? '#111827' : done ? '#f3f4f6' : '#ffffff',
                      color: active ? '#ffffff' : done ? '#374151' : '#9ca3af',
                      fontWeight: 800,
                      fontSize: '12.5px',
                      cursor: 'pointer',
                    }}
                  >
                    <span>{done ? <svg width='14' height='14' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2' strokeLinecap='round' strokeLinejoin='round' style={{display: 'inline', verticalAlign: 'middle'}}><polyline points='20 6 9 17 4 12'/></svg> : s}.</span>
                    <span>{label}</span>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Step Body */}
          <div style={{ padding: '32px' }}>
            {mode === 'host' ? (
              /* HOST STEPS */
              <div>
                {step === 1 && (
                  <div>
                    <div style={{ marginBottom: '24px' }}>
                      <span style={{ fontSize: '12px', fontWeight: 800, color: '#7c3aed', background: '#f3e8ff', padding: '3px 8px', borderRadius: '6px' }}>
                        STEP 1 OF 4: SPORT SELECTION
                      </span>
                      <h2 style={{ fontSize: '24px', fontWeight: 900, color: '#111827', margin: '8px 0 6px' }}>
                        Select Your Sport & Experience Tier
                      </h2>
                      <p style={{ fontSize: '14px', color: '#6b7280', margin: 0, lineHeight: 1.5 }}>
                        Pick the sport you want to host. Setting the right skill expectation (Casual vs Competitive) prevents lopsided matchups and ensures players have fun.
                      </p>
                    </div>

                    <div style={{ background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: '16px', padding: '20px', marginBottom: '20px' }}>
                      <div style={{ fontSize: '11px', fontWeight: 800, color: '#6b7280', textTransform: 'uppercase', marginBottom: '12px' }}>
                        Live Simulation (Tap to select):
                      </div>
                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: '10px', marginBottom: '16px' }}>
                        {[
                          { id: 'cricket', name: 'Box Cricket', icon: <svg width='1em' height='1em' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2' strokeLinecap='round' strokeLinejoin='round'><path d='M18.8 4L20 5.2a1.4 1.4 0 010 2L10 17.2a1.4 1.4 0 01-2 0L6.8 16a1.4 1.4 0 010-2L16.8 4a1.4 1.4 0 012 0z'/><path d='M4 20h.01'/><path d='M8 20h.01'/><path d='M6 18h.01'/></svg>, players: '10-14 players' },
                          { id: 'football', name: '5v5 Football', icon: <svg width='1em' height='1em' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2' strokeLinecap='round' strokeLinejoin='round'><circle cx='12' cy='12' r='10'/><path d='M12 7l-3.5 6h7z'/><path d='M12 7l3.5 6 4-2'/><path d='M12 7l-3.5 6-4-2'/><path d='M4.5 11l4 2 1.5 5'/><path d='M19.5 11l-4 2-1.5 5'/></svg>, players: '10-12 players' },
                          { id: 'badminton', name: 'Badminton Doubles', icon: <svg width='1em' height='1em' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2' strokeLinecap='round' strokeLinejoin='round'><path d='M18.2 5.8a4.5 4.5 0 00-6.4 0L4 13.6c-2.7 2.7-2.7 7.1 0 9.8a7 7 0 009.8 0L21.6 15.6a4.5 4.5 0 000-6.4l-3.4-3.4z'/><path d='M13.6 13.6l3.4-3.4'/><path d='M10.2 17l3.4-3.4'/></svg>, players: '4 players' },
                          { id: 'basketball', name: 'Basketball Half-Court', icon: <svg width='1em' height='1em' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2' strokeLinecap='round' strokeLinejoin='round'><circle cx='12' cy='12' r='10'/><path d='M8.56 2.75c4.37 6.03 6.02 9.42 3.42 17.02'/><path d='M15.44 2.75c-4.37 6.03-6.02 9.42-3.42 17.02'/><path d='M2.75 8.56c6.03-4.37 9.42-6.02 17.02-3.42'/><path d='M2.75 15.44c6.03 4.37 9.42 6.02 17.02 3.42'/></svg>, players: '6 players' },
                        ].map((s) => (
                          <div
                            key={s.id}
                            onClick={() => setMockSport(s.id)}
                            style={{
                              padding: '14px',
                              borderRadius: '14px',
                              border: mockSport === s.id ? '2px solid #7c3aed' : '1px solid #e5e7eb',
                              background: mockSport === s.id ? '#f5f3ff' : '#ffffff',
                              cursor: 'pointer',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '10px',
                            }}
                          >
                            <span style={{ fontSize: '22px' }}>{s.icon}</span>
                            <div>
                              <div style={{ fontSize: '13.5px', fontWeight: 800, color: mockSport === s.id ? '#7c3aed' : '#111827' }}>{s.name}</div>
                              <div style={{ fontSize: '11px', color: '#6b7280' }}>{s.players}</div>
                            </div>
                          </div>
                        ))}
                      </div>

                      <div style={{ display: 'flex', gap: '10px' }}>
                        {['casual', 'intermediate', 'competitive'].map((lvl) => (
                          <button
                            key={lvl}
                            onClick={() => setMockSkill(lvl)}
                            style={{
                              flex: 1,
                              padding: '10px',
                              borderRadius: '10px',
                              border: mockSkill === lvl ? '2px solid #111827' : '1px solid #e5e7eb',
                              background: mockSkill === lvl ? '#111827' : '#ffffff',
                              color: mockSkill === lvl ? '#ffffff' : '#374151',
                              fontSize: '12px',
                              fontWeight: 800,
                              textTransform: 'capitalize',
                              cursor: 'pointer',
                            }}
                          >
                            {lvl} Level
                          </button>
                        ))}
                      </div>
                    </div>
                  </div>
                )}

                {step === 2 && (
                  <div>
                    <div style={{ marginBottom: '24px' }}>
                      <span style={{ fontSize: '12px', fontWeight: 800, color: '#7c3aed', background: '#f3e8ff', padding: '3px 8px', borderRadius: '6px' }}>
                        STEP 2 OF 4: VENUE & 7-DAY CALENDAR
                      </span>
                      <h2 style={{ fontSize: '24px', fontWeight: 900, color: '#111827', margin: '8px 0 6px' }}>
                        Pick Turf Ground & 7-Day Booking Slot
                      </h2>
                      <p style={{ fontSize: '14px', color: '#6b7280', margin: 0, lineHeight: 1.5 }}>
                        Lobbies on KhelDrop can be booked strictly across a rolling 7-day calendar (Today through the next 6 days) to prevent dead matches.
                      </p>
                    </div>

                    <div style={{ background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: '16px', padding: '20px', marginBottom: '20px' }}>
                      <div style={{ display: 'flex', gap: '10px', overflowX: 'auto', marginBottom: '16px' }}>
                        {[
                          { id: 'today', d: 'Today', date: 'Oct 5' },
                          { id: 'tomorrow', d: 'Tomorrow', date: 'Oct 6' },
                          { id: 'wed', d: 'Wed', date: 'Oct 7' },
                          { id: 'thu', d: 'Thu', date: 'Oct 8' },
                          { id: 'fri', d: 'Fri', date: 'Oct 9' },
                        ].map((day) => (
                          <div
                            key={day.id}
                            onClick={() => setMockDay(day.id)}
                            style={{
                              minWidth: '85px',
                              padding: '12px',
                              borderRadius: '12px',
                              border: mockDay === day.id ? '2px solid #7c3aed' : '1px solid #e5e7eb',
                              background: mockDay === day.id ? '#7c3aed' : '#ffffff',
                              color: mockDay === day.id ? '#ffffff' : '#111827',
                              textAlign: 'center',
                              cursor: 'pointer',
                            }}
                          >
                            <div style={{ fontSize: '11px', fontWeight: 600 }}>{day.d}</div>
                            <div style={{ fontSize: '14px', fontWeight: 800 }}>{day.date}</div>
                          </div>
                        ))}
                      </div>

                      <div style={{ background: '#ffffff', border: '1px solid #e5e7eb', borderRadius: '12px', padding: '14px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                          <div style={{ width: '36px', height: '36px', borderRadius: '10px', background: 'rgba(124, 58, 237, 0.1)', color: '#7c3aed', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <MapPin size={18} />
                          </div>
                          <div>
                            <div style={{ fontWeight: 800, fontSize: '14px', color: '#111827' }}>DDA Siri Fort Sports Complex</div>
                            <div style={{ fontSize: '12px', color: '#6b7280' }}>August Kranti Marg, New Delhi • Verified Venue Geotag</div>
                          </div>
                        </div>
                        <span style={{ fontSize: '11px', fontWeight: 800, background: '#dcfce7', color: '#166534', padding: '3px 8px', borderRadius: '6px' }}>
                          Verified
                        </span>
                      </div>
                    </div>
                  </div>
                )}

                {step === 3 && (
                  <div>
                    <div style={{ marginBottom: '24px' }}>
                      <span style={{ fontSize: '12px', fontWeight: 800, color: '#7c3aed', background: '#f3e8ff', padding: '3px 8px', borderRadius: '6px' }}>
                        STEP 3 OF 4: SLOTS & LEGAL RULES
                      </span>
                      <h2 style={{ fontSize: '24px', fontWeight: 900, color: '#111827', margin: '8px 0 6px' }}>
                        Set Player Squad Capacity & Zero Betting Rules
                      </h2>
                      <p style={{ fontSize: '14px', color: '#6b7280', margin: 0, lineHeight: 1.5 }}>
                        As host, you fill Slot #1 automatically. Configure slot limits and affirm zero-gambling compliance.
                      </p>
                    </div>

                    <div style={{ background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: '16px', padding: '20px', marginBottom: '20px' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '10px' }}>
                        <span style={{ fontWeight: 800, fontSize: '13px' }}>10 Total Player Slots</span>
                        <span style={{ fontWeight: 800, fontSize: '12px', color: '#ef4444' }}><svg width='14' height='14' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2' strokeLinecap='round' strokeLinejoin='round' style={{display: 'inline', verticalAlign: 'middle', marginRight: 4}}><path d='M8.5 14.5A2.5 2.5 0 0011 12c0-1.38-.5-2-1-3-1.072-2.143-.224-4.054 2-6 .5 2.5 2 4.9 4 6.5 2 1.6 3 3.5 3 5.5a7 7 0 11-14 0c0-1.153.433-2.294 1-3a2.5 2.5 0 002.5 2.5z'/></svg> 1 Slot Left to Kickoff</span>
                      </div>
                      <div style={{ display: 'flex', gap: '6px', marginBottom: '16px' }}>
                        <div style={{ flex: 1, padding: '10px 4px', background: '#111827', color: '#ffffff', borderRadius: '8px', textAlign: 'center', fontSize: '11px', fontWeight: 800 }}>#1 HOST</div>
                        {[2, 3, 4, 5, 6, 7, 8, 9].map((s) => (
                          <div key={s} style={{ flex: 1, padding: '10px 4px', background: '#dcfce7', color: '#166534', borderRadius: '8px', textAlign: 'center', fontSize: '11px', fontWeight: 800 }}>#{s}</div>
                        ))}
                        <div style={{ flex: 1, padding: '10px 4px', background: '#fee2e2', border: '1.5px dashed #ef4444', color: '#b91c1c', borderRadius: '8px', textAlign: 'center', fontSize: '11px', fontWeight: 800 }}>#10 NEED 1</div>
                      </div>

                      <div style={{ background: '#fef2f2', border: '1px solid #fecaca', padding: '12px 14px', borderRadius: '10px', display: 'flex', gap: '8px' }}>
                        <Shield size={16} color="#dc2626" style={{ flexShrink: 0, marginTop: '2px' }} />
                        <div style={{ fontSize: '12px', color: '#991b1b', lineHeight: 1.45 }}>
                          <strong>Zero Gambling Statutory Rule:</strong> Betting or money wagering is strictly banned under Indian law. KhelDrop matches are for sport and fitness only.
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {step === 4 && (
                  <div>
                    <div style={{ marginBottom: '24px' }}>
                      <span style={{ fontSize: '12px', fontWeight: 800, color: '#10b981', background: '#d1fae5', padding: '3px 8px', borderRadius: '6px' }}>
                        STEP 4 OF 4: PUBLISH & COORDINATE
                      </span>
                      <h2 style={{ fontSize: '24px', fontWeight: 900, color: '#111827', margin: '8px 0 6px' }}>
                        Broadcast on Radar & Chat with Your Squad
                      </h2>
                      <p style={{ fontSize: '14px', color: '#6b7280', margin: 0, lineHeight: 1.5 }}>
                        Once published, your lobby goes live on the map. Squad chat unlocks so everyone knows who is bringing balls/gear.
                      </p>
                    </div>

                    <div style={{ background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: '16px', padding: '20px', marginBottom: '20px' }}>
                      <div style={{ background: '#ffffff', border: '1px solid #e5e7eb', borderRadius: '14px', padding: '16px' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                          <span style={{ background: '#111827', color: '#ffffff', fontSize: '11px', fontWeight: 800, padding: '2px 8px', borderRadius: '6px' }}>LIVE MATCH LOBBY</span>
                          <span style={{ color: '#7c3aed', fontWeight: 800, fontSize: '12px' }}>Kickoff Today at 6:30 PM</span>
                        </div>
                        <h3 style={{ fontSize: '17px', fontWeight: 900, margin: '4px 0' }}>Weekend 5v5 Football Derby</h3>
                        <p style={{ fontSize: '12.5px', color: '#6b7280', margin: '0 0 14px' }}>Yamuna Sports Complex • 9 of 10 slots filled</p>
                        <div style={{ display: 'flex', gap: '8px' }}>
                          <button style={{ flex: 1, padding: '9px', background: '#f3f4f6', border: '1px solid #e5e7eb', borderRadius: '8px', fontSize: '12.5px', fontWeight: 800, cursor: 'pointer' }}>
                            <svg width='14' height='14' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2' strokeLinecap='round' strokeLinejoin='round' style={{display: 'inline', verticalAlign: 'middle', marginRight: 4}}><path d='M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z'/></svg> Open Match Chat
                          </button>
                          <button style={{ flex: 1, padding: '9px', background: '#10b981', color: '#ffffff', border: 'none', borderRadius: '8px', fontSize: '12.5px', fontWeight: 800, cursor: 'pointer' }}>
                            <svg width='14' height='14' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2' strokeLinecap='round' strokeLinejoin='round' style={{display: 'inline', verticalAlign: 'middle', marginRight: 4}}><polyline points='20 6 9 17 4 12'/></svg> Turf Arrival Check-In
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            ) : (
              /* JOIN STEPS */
              <div>
                {step === 1 && (
                  <div>
                    <div style={{ marginBottom: '24px' }}>
                      <span style={{ fontSize: '12px', fontWeight: 800, color: '#10b981', background: '#d1fae5', padding: '3px 8px', borderRadius: '6px' }}>
                        STEP 1 OF 4: RADAR DISCOVERY
                      </span>
                      <h2 style={{ fontSize: '24px', fontWeight: 900, color: '#111827', margin: '8px 0 6px' }}>
                        Discover Games on Live Radar & Sport Filters
                      </h2>
                      <p style={{ fontSize: '14px', color: '#6b7280', margin: 0, lineHeight: 1.5 }}>
                        Search for open matches near you. Toggle the "Need 1 Player" filter when you want to join an active team ready to play right now!
                      </p>
                    </div>

                    <div style={{ background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: '16px', padding: '20px', marginBottom: '20px' }}>
                      <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                        <button
                          onClick={() => setMockFilter('all')}
                          style={{
                            padding: '8px 14px',
                            borderRadius: '10px',
                            border: mockFilter === 'all' ? '2px solid #111827' : '1px solid #e5e7eb',
                            background: mockFilter === 'all' ? '#111827' : '#ffffff',
                            color: mockFilter === 'all' ? '#ffffff' : '#111827',
                            fontWeight: 800,
                            fontSize: '12.5px',
                            cursor: 'pointer',
                          }}
                        >
                          All Sports (16)
                        </button>
                        <button
                          onClick={() => setMockFilter('need1')}
                          style={{
                            padding: '8px 14px',
                            borderRadius: '10px',
                            border: mockFilter === 'need1' ? '2px solid #ef4444' : '1px solid #fecaca',
                            background: mockFilter === 'need1' ? '#fef2f2' : '#ffffff',
                            color: '#dc2626',
                            fontWeight: 800,
                            fontSize: '12.5px',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '6px',
                          }}
                        >
                          <Flame size={14} color="#dc2626" /> Only Games Needing 1 Player (4)
                        </button>
                      </div>
                    </div>
                  </div>
                )}

                {step === 2 && (
                  <div>
                    <div style={{ marginBottom: '24px' }}>
                      <span style={{ fontSize: '12px', fontWeight: 800, color: '#10b981', background: '#d1fae5', padding: '3px 8px', borderRadius: '6px' }}>
                        STEP 2 OF 4: MATCH INTEL
                      </span>
                      <h2 style={{ fontSize: '24px', fontWeight: 900, color: '#111827', margin: '8px 0 6px' }}>
                        Check Match Details & Ground Directions
                      </h2>
                      <p style={{ fontSize: '14px', color: '#6b7280', margin: 0, lineHeight: 1.5 }}>
                        Review distance, start time, host badge, turf amenities, and launch 1-tap Google Maps directions to the venue.
                      </p>
                    </div>

                    <div style={{ background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: '16px', padding: '20px', marginBottom: '20px' }}>
                      <div style={{ background: '#ffffff', border: '1px solid #e5e7eb', borderRadius: '14px', padding: '16px' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '6px' }}>
                          <span style={{ background: '#fee2e2', color: '#dc2626', padding: '2px 8px', borderRadius: '6px', fontSize: '11px', fontWeight: 800, display: 'flex', alignItems: 'center', gap: '4px' }}>
                            <Flame size={12} /> ONE MORE NEEDED
                          </span>
                          <span style={{ color: '#16a34a', fontWeight: 800, fontSize: '12px' }}>Starts in 40m</span>
                        </div>
                        <h3 style={{ fontSize: '17px', fontWeight: 900, margin: '6px 0 4px' }}>5v5 Turf Football Match</h3>
                        <div style={{ display: 'flex', gap: '10px', fontSize: '12.5px', color: '#6b7280', marginBottom: '12px' }}>
                          <span><svg width='14' height='14' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2' strokeLinecap='round' strokeLinejoin='round' style={{display: 'inline', verticalAlign: 'middle', marginRight: 4}}><path d='M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0118 0z'/><circle cx='12' cy='10' r='3'/></svg> 1.2 km away</span>
                          <span>•</span>
                          <span>Host: Ashutosh (Verified)</span>
                        </div>
                        <button style={{ padding: '6px 12px', background: '#f3f4f6', border: '1px solid #e5e7eb', borderRadius: '6px', fontSize: '12px', fontWeight: 800, cursor: 'pointer' }}>
                          Maps Directions ↗
                        </button>
                      </div>
                    </div>
                  </div>
                )}

                {step === 3 && (
                  <div>
                    <div style={{ marginBottom: '24px' }}>
                      <span style={{ fontSize: '12px', fontWeight: 800, color: '#10b981', background: '#d1fae5', padding: '3px 8px', borderRadius: '6px' }}>
                        STEP 3 OF 4: SAFETY WAIVER
                      </span>
                      <h2 style={{ fontSize: '24px', fontWeight: 900, color: '#111827', margin: '8px 0 6px' }}>
                        1-Tap Pre-Match Safety Waiver
                      </h2>
                      <p style={{ fontSize: '14px', color: '#6b7280', margin: 0, lineHeight: 1.5 }}>
                        Under Indian Law (*Volenti non fit injuria*), players voluntarily accept physical sports exertion risks. A quick digital checkmark secures legal compliance.
                      </p>
                    </div>

                    <div style={{ background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: '16px', padding: '20px', marginBottom: '20px' }}>
                      <div style={{ background: '#ffffff', border: '1.5px solid #7c3aed', borderRadius: '12px', padding: '16px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                          <Shield size={18} color="#7c3aed" />
                          <span style={{ fontWeight: 800, fontSize: '13.5px', color: '#111827' }}>
                            Statutory Athletic Safety & Fitness Waiver
                          </span>
                        </div>
                        <p style={{ fontSize: '12.5px', color: '#4b5563', margin: '0 0 12px', lineHeight: 1.5 }}>
                          I affirm that I am 18+ years old, fit to play, participate of my own free will, and recognize that KhelDrop acts purely as a digital facilitator.
                        </p>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <input
                            type="checkbox"
                            id="guide-page-waiver"
                            checked={mockWaiverChecked}
                            onChange={(e) => setMockWaiverChecked(e.target.checked)}
                            style={{ accentColor: '#7c3aed', cursor: 'pointer' }}
                          />
                          <label htmlFor="guide-page-waiver" style={{ fontSize: '12.5px', fontWeight: 800, cursor: 'pointer' }}>
                            I solemnly affirm and accept the Match Safety Waiver
                          </label>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {step === 4 && (
                  <div>
                    <div style={{ marginBottom: '24px' }}>
                      <span style={{ fontSize: '12px', fontWeight: 800, color: '#10b981', background: '#d1fae5', padding: '3px 8px', borderRadius: '6px' }}>
                        STEP 4 OF 4: LOCK SLOT & PLAY
                      </span>
                      <h2 style={{ fontSize: '24px', fontWeight: 900, color: '#111827', margin: '8px 0 6px' }}>
                        Slot Locked, Squad Chat & Ground Arrival
                      </h2>
                      <p style={{ fontSize: '14px', color: '#6b7280', margin: 0, lineHeight: 1.5 }}>
                        Once confirmed, your slot cannot be taken. Chat with the squad to coordinate, arrive on time, and play!
                      </p>
                    </div>

                    <div style={{ background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: '16px', padding: '24px', textAlign: 'center', marginBottom: '20px' }}>
                      <div style={{ width: '48px', height: '48px', borderRadius: '50%', background: '#dcfce7', color: '#16a34a', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 12px' }}>
                        <Check size={26} strokeWidth={3} />
                      </div>
                      <h3 style={{ fontSize: '18px', fontWeight: 900, margin: '0 0 6px' }}>Slot Reserved & Confirmed!</h3>
                      <p style={{ fontSize: '13px', color: '#6b7280', margin: '0 0 16px' }}>You are player #10 of 10. The squad is complete!</p>
                      <button
                        onClick={() => navigate('/')}
                        style={{
                          background: '#0a0a0b',
                          color: '#ffffff',
                          border: 'none',
                          padding: '12px 24px',
                          borderRadius: '12px',
                          fontWeight: 800,
                          fontSize: '14px',
                          cursor: 'pointer',
                        }}
                      >
                        Explore Live Games Now
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Step Controls */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '24px', paddingTop: '20px', borderTop: '1px solid #f3f4f6' }}>
              <button
                type="button"
                onClick={() => setStep((p) => Math.max(1, p - 1))}
                disabled={step === 1}
                style={{
                  padding: '10px 18px',
                  borderRadius: '10px',
                  border: '1px solid #e5e7eb',
                  background: '#ffffff',
                  color: step === 1 ? '#d1d5db' : '#111827',
                  fontWeight: 700,
                  fontSize: '13px',
                  cursor: step === 1 ? 'not-allowed' : 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                }}
              >
                <ArrowLeft size={15} /> Previous Step
              </button>

              <button
                type="button"
                onClick={() => {
                  if (step < totalSteps) {
                    setStep((p) => p + 1);
                  } else {
                    navigate('/');
                  }
                }}
                style={{
                  padding: '10px 22px',
                  borderRadius: '10px',
                  border: 'none',
                  background: mode === 'host' ? '#7c3aed' : '#10b981',
                  color: '#ffffff',
                  fontWeight: 800,
                  fontSize: '13.5px',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                }}
              >
                {step < totalSteps ? (
                  <>Next Step <ArrowRight size={15} /></>
                ) : mode === 'host' ? (
                  <>Create Match on App <Trophy size={15} /></>
                ) : (
                  <>Find Matches Near Me <Compass size={15} /></>
                )}
              </button>
            </div>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}
